
import {Component, Prop, Watch} from "vue-property-decorator";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";
import Draggable from "vuedraggable";
import {AbstractFormWizard} from "@/abstract_classes/AbstractFormWizard";
import {RaporEntity} from "@/entity/RaporEntity";
import RaporKosulPicker from "@/components/pickers/RaporKosulPicker.vue";
import Tutar from "@/components/inputs/Tutar.vue";
import {RaporAlanEntity} from "@/entity/RaporAlanEntity";
import VergiNumarasi from "@/components/inputs/VergiNumarasi.vue";
import IcraDairesiPicker from "@/components/pickers/IcraDairesiPicker.vue";
import TelefonNumarasi from "@/components/inputs/TelefonNumarasi.vue";
import DosyaEsasNumarasi from "@/components/inputs/DosyaEsasNumarasi.vue";
import Dates from "@/components/inputs/Dates.vue";
import TebligatDurumuPicker from "@/components/pickers/TebligatDurumuPicker.vue";
import TakipTipiPicker from "@/components/pickers/TakipTipiPicker.vue";
import TakipAltTipiPicker from "@/components/pickers/TakipAltTipiPicker.vue";
import TakipYoluPicker from "@/components/pickers/TakipYoluPicker.vue";
import RaporQueryBuilder from "@/components/inputs/RaporQueryBuilder.vue";
import {raporAlanGrubu, RaporAlanGrupSirasi, TemelAlanKodlari} from "@/enum/RaporAlanGrup";
import {raporAlanAciklamasi} from "@/enum/RaporAlanAciklama";

@Component({
  components: {
    RaporQueryBuilder,
    TakipYoluPicker,
    TakipAltTipiPicker,
    TakipTipiPicker,
    TebligatDurumuPicker,
    Dates,
    DosyaEsasNumarasi,
    TelefonNumarasi,
    RaporKosulPicker,
    DeleteDialog,
    Draggable,
    Tutar,
    VergiNumarasi,
    IcraDairesiPicker
  }
})

export default class RaporForm extends AbstractFormWizard {
  localValue!: RaporEntity;

  /** AI taslağı doğrudan özet adımında açılsın diye (tasarımdaki akış). */
  @Prop({default: 1}) baslangicAdim!: number;

  activeStep: number = 1;
  rapor_alanlari_items: Array<RaporAlanEntity> = [];
  ready = false;
  onFly = false;
  alanArama: string = "";
  drag = false;

  /** Kullanıcının elle açıp kapattığı gruplar; yoksa varsayılan kural işler. */
  acikGruplar: { [baslik: string]: boolean } = {};

  adimlar = [
    {ad: "Alan & Künye", ikon: "mdi-format-list-checks"},
    {ad: "Sıralama", ikon: "mdi-sort"},
    {ad: "Koşullar", ikon: "mdi-tune-variant"},
    {ad: "Özet & Grafik", ikon: "mdi-chart-box-outline"},
  ];

  stepperHeaders = [
    "Alan & Künye",
    "Sıralama",
    "Koşullar",
    "Özet & Grafik"
  ];

  get seciliAlanSayisi(): number {
    return this.localValue.rapor_alanlari ? this.localValue.rapor_alanlari.length : 0;
  }

  get kaydedilebilir(): boolean {
    return this.seciliAlanSayisi > 0 && !!(this.localValue.isim && String(this.localValue.isim).trim());
  }

  get kosulSayisi(): number {
    const say = (grup: any): number => {
      if (!grup || !grup.kosullar) return 0;
      return grup.kosullar.reduce((t: number, k: any) => t + (k.kosullar ? say(k) : 1), 0);
    };
    return say(this.localValue.rapor_secilen_kosullar);
  }

  alanAdi(id: number): string {
    const alan = this.rapor_alanlari_items.find((v: any) => v.id == id);
    return alan ? alan.isim : "";
  }

  alanAciklamasi(kod: string): string {
    return raporAlanAciklamasi(kod);
  }

  // Katalogdaki alanları istemci tarafında gruplar; arama metnine göre süzer.
  get grupluAlanlar(): Array<{ baslik: string; alanlar: Array<RaporAlanEntity>; seciliSayi: number }> {
    const arama = (this.alanArama || "").toLocaleLowerCase("tr-TR");
    const gruplar: { [baslik: string]: Array<RaporAlanEntity> } = {};
    this.rapor_alanlari_items.forEach((alan: any) => {
      if (arama && !(alan.isim || "").toLocaleLowerCase("tr-TR").includes(arama)) return;
      const baslik = raporAlanGrubu(alan.kod);
      if (!gruplar[baslik]) gruplar[baslik] = [];
      gruplar[baslik].push(alan);
    });
    return RaporAlanGrupSirasi
        .filter((baslik) => gruplar[baslik] && gruplar[baslik].length > 0)
        .map((baslik) => ({
          baslik,
          alanlar: gruplar[baslik],
          seciliSayi: gruplar[baslik].filter((a: any) => this.alanSecili(a.id)).length,
        }));
  }

  /**
   * Katalogda 100'ün üzerinde alan var; tasarımdaki "hepsi açık" düzeni burada
   * ekranlarca uzun bir sayfa üretiyordu. Açılışta ilk üç grup açık gelir,
   * arama yazıldığında eşleşmeyi göstermek için hepsi açılır.
   */
  grupAcikMi(baslik: string, index: number): boolean {
    if (this.alanArama) return true;
    if (Object.prototype.hasOwnProperty.call(this.acikGruplar, baslik)) return this.acikGruplar[baslik];
    return index < 3;
  }

  grupToggle(baslik: string, index: number) {
    this.$set(this.acikGruplar, baslik, !this.grupAcikMi(baslik, index));
  }

  alanSecili(id: number): boolean {
    return !!this.localValue.rapor_alanlari && this.localValue.rapor_alanlari.indexOf(id) > -1;
  }

  alanToggle(id: number) {
    if (!this.localValue.rapor_alanlari) this.$set(this.localValue, "rapor_alanlari", []);
    const index = this.localValue.rapor_alanlari.indexOf(id);
    if (index > -1) {
      this.localValue.rapor_alanlari.splice(index, 1);
    } else {
      // seçim sırası korunur; 2. adımdaki sıralamanın başlangıcı budur
      this.localValue.rapor_alanlari.push(id);
    }
    this.input();
  }

  tumunuSec() {
    this.localValue.rapor_alanlari = this.rapor_alanlari_items.map((a: any) => a.id);
    this.input();
  }

  temelAlanlariSec() {
    const ids = this.rapor_alanlari_items
        .filter((a: any) => TemelAlanKodlari.indexOf(a.kod) > -1)
        .map((a: any) => a.id);
    this.localValue.rapor_alanlari = ids;
    this.input();
  }

  grafikSecenekleri = [
    {deger: "bar", baslik: "Çubuk Grafik (Bar)", aciklama: "Kategorik karşılaştırmalar için", ikon: "mdi-chart-bar"},
    {deger: "pie", baslik: "Pasta Grafik (Pie)", aciklama: "Oransal dağılım analizi için", ikon: "mdi-chart-pie"},
    {deger: "kartlar", baslik: "Sadece Özet Kartları", aciklama: "Sadece rakamsal toplamlar", ikon: "mdi-card-text-outline"},
  ];

  get grafikTercihi(): string {
    return this.localValue.grafik_tercihi || "kartlar";
  }

  grafikSec(deger: string) {
    this.$set(this.localValue, "grafik_tercihi", deger);
    this.input();
  }

  secimiTemizle() {
    this.localValue.rapor_alanlari = [];
    this.input();
  }

  get dragOptions() {
    return {
      animation: 200,
      group: "description",
      disabled: false,
      ghostClass: "ghost"
    };
  }

  adimaGit(adim: number) {
    this.activeStep = adim;
  }

  komsuAdim(index: number, yon: number) {
    const hedef = (index + yon + this.adimlar.length) % this.adimlar.length;
    this.adimaGit(hedef + 1);
    this.$nextTick(() => {
      const ref: any = (this.$refs["adim" + hedef] as any);
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && el.focus) el.focus();
    });
  }

  onClose() {
    this.$emit("onClose");
  }

  @Watch('localValue.alanlar')
  onAlanlarChange() {
    if (this.localValue.alanlar && this.ready) {
      this.reformatingLocalValue();
    }
  }

  created() {
    this.ready = false;
    this.activeStep = this.baslangicAdim || 1;
    // Mixin localValue'yu düz obje kurar (RaporEntity değil); rapor_alanlari
    // sonradan düz atamayla eklenirse Vue 2'de reaktif olmaz ve sayaç/grup
    // computed'ları güncellenmez. Ekleme modunda diziyi $set ile reaktif kur
    // (Kart No varsayılan seçili — entity'deki davranışla aynı).
    if (!this.localValue.rapor_alanlari || this.localValue.rapor_alanlari.length === 0) {
      this.$set(this.localValue, "rapor_alanlari", [1]);
    }
    if (!this.localValue.rapor_secilen_kosullar) {
      this.$set(this.localValue, "rapor_secilen_kosullar", {ve_mi: true, kosullar: []});
    }
    this.load();
  }

  reformatingLocalValue() {
    let value = new RaporEntity();
    value.id = this.localValue.id;
    value.isim = this.localValue.isim;
    value.kategori = this.localValue.kategori ?? null;
    value.aciklama = this.localValue.aciklama ?? null;
    value.grafik_tercihi = this.localValue.grafik_tercihi ?? null;
    value.rapor_alanlari = [];
    this.localValue.alanlar.forEach((alan: any) => {
      value.rapor_alanlari.push(alan.rapor_alan_id);
    })
    value.rapor_secilen_kosullar = this.localValue.rapor_secilen_kosullar;
    this.localValue = value;
    this.input();
  }

  save() {
    if (!this.kaydedilebilir) return;
    this.onFly = true
    if (this.localValue.id) {
      this.$http.put('/api/v1/rapor/' + this.localValue.id, this.localValue).then(() => {
        this.activeStep = 1;
        this.$emit('onSuccess');
      }).finally(() => {
        this.onFly = false;
      });
    } else {
      this.$http.post('/api/v1/rapor', this.localValue).then(() => {
        this.resetValue();
        this.$emit('onSuccess');
        this.localValue.rapor_alanlari = [1];
      }).finally(() => {
        this.onFly = false;
      });
    }
  }

  load() {
    this.$helper.requestWithCache('/api/v1/rapor-alan').then(responce => {
      this.rapor_alanlari_items = responce.data;
      if (this.localValue.id) {
        this.reformatingLocalValue();
        this.ready = true;
      }
    })
  }

  remove(item: any) {
    this.localValue.rapor_alanlari.splice(this.localValue.rapor_alanlari.indexOf(item.id), 1)
  }

  nextStepRules() {
    return true;
  }

  previousStepRules() {
    return true;
  }

  async close() {
    await this.resetValue();
    this.$emit('onClose');
  }

  async resetValue() {
    this.activeStep = 1;
    if (!this.localValue.id) {
      this.localValue = new RaporEntity();
      this.localValue.rapor_secilen_kosullar.kosullar = [];
      this.input();
    }
  }
}
